import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Download, Search, Loader2, ArrowUp, ArrowDown, Sparkles, X } from 'lucide-react';

import AnalyticsClientCard from './AnalyticsClientCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'new',       label: 'New' },
  { id: 'top',       label: 'Top' },
  { id: 'returning', label: 'Returning' },
  { id: 'dormant',   label: 'Dormant' },
];

const COLUMNS = {
  new: [
    { key: 'name',            label: 'Client',         sortable: true },
    { key: 'registered_at',   label: 'Registered',     sortable: true,  align: 'left' },
    { key: 'orders_count',    label: 'Orders',         sortable: true,  align: 'right' },
    { key: 'ordered_amount',  label: 'Ordered amount', sortable: true,  align: 'right', money: true },
    { key: 'first_order_at',  label: 'First order',    sortable: true },
    { key: 'topped_up',       label: 'Topped up',      sortable: false, align: 'center' },
  ],
  top: [
    { key: 'name',            label: 'Client',         sortable: true },
    { key: 'orders_count',    label: 'Orders',         sortable: true,  align: 'right' },
    { key: 'ordered_amount',  label: 'Ordered amount', sortable: true,  align: 'right', money: true },
    { key: 'avg_check',       label: 'Avg check',      sortable: true,  align: 'right', money: true },
    { key: 'share_pct',       label: 'Share %',        sortable: true,  align: 'right' },
    { key: 'lifetime_spent',  label: 'All-time spent', sortable: true,  align: 'right', money: true },
    { key: 'top_items',       label: 'Ordered',        sortable: false },
  ],
  returning: [
    { key: 'name',            label: 'Client',         sortable: true },
    { key: 'orders_count',    label: 'Orders',         sortable: true,  align: 'right' },
    { key: 'ordered_amount',  label: 'Ordered amount', sortable: true,  align: 'right', money: true },
    { key: 'avg_check',       label: 'Avg check',      sortable: true,  align: 'right', money: true },
    { key: 'last_order_at',   label: 'Last order',     sortable: true },
    { key: 'lifetime_spent',  label: 'All-time spent', sortable: true,  align: 'right', money: true },
  ],
  dormant: [
    { key: 'name',            label: 'Client',         sortable: true },
    { key: 'last_order_at',   label: 'Last order',     sortable: true },
    { key: 'days_since',      label: 'Days since',     sortable: true,  align: 'right' },
    { key: 'lifetime_orders', label: 'Lifetime orders', sortable: true, align: 'right' },
    { key: 'lifetime_spent',  label: 'Lifetime spent', sortable: true,  align: 'right', money: true },
    { key: 'credit_balance',  label: 'Credit balance', sortable: true,  align: 'right', money: true },
  ],
};

const money = (n) => `€${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-US');

/**
 * Clients block for the Analytics page — one card with four segment tabs
 * instead of four separate cards. Everything (segmentation, sorting,
 * search, paging) is computed server-side; a row click opens the client
 * card drawer without leaving the page.
 */
export default function AnalyticsClientsBlock({ token, range, customStart, customEnd, filters, activeTab, onlyWithOrdersDefault = true, onTabChange }) {
  const [tab, setTab] = useState(activeTab || 'new');
  const [onlyWithOrders, setOnlyWithOrders] = useState(true);
  const [topN, setTopN] = useState(10);
  const [dormantDays, setDormantDays] = useState(90);
  const [sort, setSort] = useState('ordered_amount');
  const [order, setOrder] = useState('desc');
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openClient, setOpenClient] = useState(() => new URLSearchParams(window.location.search).get('client'));

  // Keep ?client=<id> in the URL so a card can be shared / restored, and
  // let ↑/↓ walk the table without closing the drawer.
  useEffect(() => {
    const u = new URL(window.location.href);
    if (openClient) u.searchParams.set('client', openClient);
    else u.searchParams.delete('client');
    window.history.replaceState({}, '', u);
  }, [openClient]);

  useEffect(() => { if (activeTab && activeTab !== tab) setTab(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // KPI click-through decides the New-tab mode ("New clients" → with orders,
  // "Registrations" → every sign-up, including the ones that never ordered).
  useEffect(() => { setOnlyWithOrders(onlyWithOrdersDefault); }, [onlyWithOrdersDefault]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set('segment', tab);
    p.set('range', range);
    if (range === 'custom') { p.set('start', customStart); p.set('end', customEnd); }
    p.set('sort', sort);
    p.set('order', order);
    p.set('limit', String(tab === 'top' ? topN : limit));
    if (debounced) p.set('q', debounced);
    if (tab === 'new') p.set('only_with_orders', onlyWithOrders ? 'true' : 'false');
    if (tab === 'dormant') p.set('dormant_days', String(dormantDays));
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v && ['manufacturer', 'ecu_type', 'status'].includes(k)) p.set(k, v);
    });
    return p.toString();
  }, [tab, range, customStart, customEnd, sort, order, limit, topN, debounced, onlyWithOrders, dormantDays, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/analytics/clients?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.ok ? await r.json() : null);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [qs, token]);

  useEffect(() => { load(); }, [load]);

  const switchTab = (id) => {
    setTab(id);
    setLimit(10);
    setSort(id === 'dormant' ? 'lifetime_spent' : 'ordered_amount');
    setOrder('desc');
    onTabChange?.(id);
  };

  const toggleSort = (key) => {
    if (sort === key) setOrder(order === 'desc' ? 'asc' : 'desc');
    else { setSort(key); setOrder('desc'); }
  };

  const exportCsv = async () => {
    const p = new URLSearchParams(qs);
    p.delete('limit');
    try {
      const r = await fetch(`${API_URL}/api/admin/analytics/clients/export.csv?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `clients_${tab}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (_) { /* silent */ }
  };

  const s = data?.summary || {};
  const items = data?.items || [];
  const cols = COLUMNS[tab];

  const summaryLine = () => {
    if (tab === 'new') {
      return (
        <>
          <b>{num(s.clients)}</b> new clients · <b>{num(s.with_orders)}</b> with orders ({s.with_orders_pct ?? 0}%)
          {' · '}revenue <b>{money(s.revenue)}</b> ({s.revenue_share_pct ?? 0}% of total)
          {s.median_days_to_first_order != null && <> · median <b>{s.median_days_to_first_order}</b> days to first order</>}
          <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-600">
            Registered {num(s.clients)} → Topped up {num(s.topped_up)} → Ordered {num(s.with_orders)}
          </span>
        </>
      );
    }
    if (tab === 'dormant') {
      return (
        <>
          <b>{num(s.clients)}</b> clients with no orders for <b>{s.dormant_days}</b>+ days ·
          lifetime revenue <b>{money(s.lifetime_spent)}</b> · unspent credits <b>{money(s.credit_balance)}</b>
          {s.median_days_since != null && <> · median <b>{s.median_days_since}</b> days since last order</>}
        </>
      );
    }
    return (
      <>
        <b>{num(s.clients)}</b> clients · <b>{num(s.orders)}</b> orders · revenue <b>{money(s.revenue)}</b>
        {' '}({s.revenue_share_pct ?? 0}% of total) · avg <b>{s.avg_orders_per_client ?? 0}</b> orders/client · avg check <b>{money(s.avg_check)}</b>
      </>
    );
  };

  const cell = (r, c) => {
    if (c.key === 'name') {
      return (
        <div className="min-w-0">
          <div className="font-bold text-slate-800 truncate flex items-center gap-1.5">
            <span className="truncate">{r.name}</span>
            {r.is_new && (
              <span title={`New client — registered ${r.registered_days_ago} days ago`} className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5" />
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {r.email}{r.account_no ? ` · #${r.account_no}` : ''}
          </div>
        </div>
      );
    }
    if (c.key === 'top_items') {
      const its = r.top_items || [];
      return (
        <div className="flex flex-wrap gap-1">
          {its.slice(0, 3).map((i) => (
            <span key={i.label} className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-semibold text-slate-600">{i.label} ×{i.count}</span>
          ))}
          {its.length > 3 && <span className="text-[10px] text-slate-400">+{its.length - 3}</span>}
        </div>
      );
    }
    if (c.key === 'topped_up') {
      return r.topped_up
        ? <span className="text-emerald-600 font-bold text-xs">yes</span>
        : <span className="text-slate-300 text-xs">no</span>;
    }
    if (c.key === 'registered_at') {
      return <span className="text-xs text-slate-600 whitespace-nowrap">{r.registered_at || '—'}{r.registered_days_ago != null && <span className="text-slate-400"> · {r.registered_days_ago}d ago</span>}</span>;
    }
    const v = r[c.key];
    if (v == null || v === '') return <span className="text-slate-300">—</span>;
    if (c.money) return <span className="font-mono tabular-nums">{money(v)}</span>;
    if (typeof v === 'number') return <span className="font-mono tabular-nums">{c.key === 'share_pct' ? `${v}%` : num(v)}</span>;
    return <span className="text-xs text-slate-600 whitespace-nowrap">{v}</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5" data-testid="an-clients-block">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-600" /> Clients
          {data?.period?.label && <span className="text-[11px] font-normal text-slate-400">{data.period.label}</span>}
        </h3>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, e-mail, account #"
              data-testid="an-clients-search"
              className="pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-lg w-[230px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            {search && (
              <button onClick={() => setSearch('')} data-testid="an-clients-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button onClick={exportCsv} data-testid="an-clients-export" className="px-2.5 py-1.5 text-xs font-bold rounded-lg bg-slate-800 hover:bg-slate-900 text-white inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            data-testid={`an-clients-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              tab === t.id ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t.label}{tab === t.id && data ? ` (${num(data.total)})` : ''}
          </button>
        ))}
        {tab === 'new' && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200">
            {[{ id: true, label: 'Only with orders' }, { id: false, label: 'All new' }].map((o) => (
              <button
                key={String(o.id)}
                onClick={() => { setOnlyWithOrders(o.id); setLimit(10); }}
                data-testid={`an-clients-new-mode-${o.id ? 'with-orders' : 'all'}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${onlyWithOrders === o.id ? 'bg-cyan-100 text-cyan-800' : 'text-slate-500 hover:bg-slate-100'}`}
              >{o.label}</button>
            ))}
          </div>
        )}
        {tab === 'top' && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200">
            <span className="text-[11px] text-slate-500 font-semibold">Top</span>
            {[10, 25, 50].map((n) => (
              <button
                key={n}
                onClick={() => setTopN(n)}
                data-testid={`an-clients-top-${n}`}
                className={`px-2 py-1 rounded-full text-[11px] font-bold ${topN === n ? 'bg-cyan-100 text-cyan-800' : 'text-slate-500 hover:bg-slate-100'}`}
              >{n}</button>
            ))}
          </div>
        )}
        {tab === 'dormant' && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200">
            <span className="text-[11px] text-slate-500 font-semibold">No orders for</span>
            {[60, 90, 180, 365].map((n) => (
              <button
                key={n}
                onClick={() => { setDormantDays(n); setLimit(10); }}
                data-testid={`an-clients-dormant-${n}`}
                className={`px-2 py-1 rounded-full text-[11px] font-bold ${dormantDays === n ? 'bg-cyan-100 text-cyan-800' : 'text-slate-500 hover:bg-slate-100'}`}
              >{n}d</button>
            ))}
          </div>
        )}
      </div>

      {/* Segment summary */}
      <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600" data-testid="an-clients-summary">
        {loading && !data ? 'Loading…' : summaryLine()}
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-3 -mx-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {cols.map((c) => (
                <th
                  key={c.key}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  data-testid={`an-clients-th-${c.key}`}
                  className={`py-2 px-2 font-bold whitespace-nowrap ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} ${c.sortable ? 'cursor-pointer hover:text-slate-800' : ''}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sort === c.key && (order === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={cols.length} className="py-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={cols.length} className="py-8 text-center text-slate-400 text-xs" data-testid="an-clients-empty">
                  No {tab} clients for {data?.period?.label || 'this period'}.
                  {tab === 'new' && onlyWithOrders && (
                    <button onClick={() => setOnlyWithOrders(false)} data-testid="an-clients-show-all-new" className="ml-2 text-cyan-700 font-bold hover:underline">
                      Show all new
                    </button>
                  )}
                </td>
              </tr>
            )}
            {!loading && items.map((r) => (
              <tr
                key={r.user_id}
                onClick={() => setOpenClient(r.user_id)}
                data-testid={`an-clients-row-${r.user_id}`}
                className="border-b border-slate-100 hover:bg-cyan-50/40 cursor-pointer"
                title="Open client card"
              >
                {cols.map((c) => (
                  <td key={c.key} className={`py-2 px-2 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'} ${c.key === 'name' ? 'max-w-[240px]' : ''}`}>
                    {cell(r, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && tab !== 'top' && items.length < (data?.total || 0) && (
        <button
          onClick={() => setLimit(limit + 25)}
          data-testid="an-clients-show-more"
          className="mt-3 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 hover:bg-slate-50"
        >
          Show 25 more ({items.length} of {num(data.total)})
        </button>
      )}

      {openClient && (
        <AnalyticsClientCard
          token={token}
          userId={openClient}
          range={range}
          customStart={customStart}
          customEnd={customEnd}
          onClose={() => setOpenClient(null)}
          onStep={(dir) => {
            const i = items.findIndex((x) => x.user_id === openClient);
            const next = items[i + dir];
            if (next) setOpenClient(next.user_id);
          }}
        />
      )}
    </div>
  );
}
